import { useAuth } from '../contexts/AuthContext';
import config from '../lib/config';

export function useApi() {
  const { getToken } = useAuth();

  const request = async (method, path, body) => {
    // Read-only archive mode: block writes before they leave the browser. The
    // backend rejects them with a 403 too, but failing fast avoids a round-trip
    // and surfaces a clear message. GET/HEAD still pass through.
    if (config.archiveMode && !['GET', 'HEAD'].includes(method)) {
      throw new Error('The tournament is over — this app is archived and read-only.');
    }
    const token = await getToken();
    const res = await fetch(`${config.apiUrl}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  return {
    getPredictions: (userId, leagueId) => request('GET', leagueId ? `/predictions/${userId}?league=${leagueId}` : `/predictions/${userId}`),
    updateProfile: (userId, body) => request('PUT', `/predictions/${userId}/profile`, body),
    adminRenamePlayer: (selfUserId, targetUserId, name) => request('PUT', `/predictions/${selfUserId}/profile`, { name, targetUserId }),
    savePrediction: (userId, matchId, body) => request('PUT', `/predictions/${userId}/match/${matchId}`, body),
    saveWinner: (userId, body) => request('PUT', `/predictions/${userId}/winner`, body),
    saveDefault: (userId, body) => request('PUT', `/predictions/${userId}/default`, body),
    getLeaderboard: (leagueId) => request('GET', leagueId ? `/leaderboard?league=${leagueId}` : '/leaderboard'),
    getLeaderboardHistory: (leagueId) => request('GET', leagueId ? `/leaderboard/history?league=${leagueId}` : '/leaderboard/history'),
    adminBackfillLeaderboard: () => request('POST', '/admin/leaderboard/backfill', {}),
    getLeagues: () => request('GET', '/leagues'),
    getLockStatus: () => request('GET', '/lock'),
    // Admin
    adminSetResult: (body) => request('POST', '/admin/result', body),
    adminGetResults: () => request('GET', '/admin/results'),
    adminSetWinner: (body) => request('PUT', '/admin/tournament/winner', body),
    adminGetUsers: () => request('GET', '/admin/users'),
    adminInvite: (body) => request('POST', '/admin/invite', body),
    adminGetLock: () => request('GET', '/admin/lock'),
    adminSetLock: (locked) => request('PUT', '/admin/lock', { locked }),
    adminExportResults: () => request('GET', '/admin/export/results'),
    adminExportPredictions: () => request('GET', '/admin/export/predictions'),
    adminUploadResults: (rows) => request('POST', '/admin/upload/results', { rows }),
    adminUploadPredictions: (rows) => request('POST', '/admin/upload/predictions', { rows }),
    adminSetUserRole: (userId, role) => request('PUT', `/admin/users/${userId}/role`, { role }),
    adminResetUserPassword: (userId) => request('POST', `/admin/users/${userId}/reset`, {}),
    adminDeleteUser: (userId) => request('POST', `/admin/users/${userId}/delete`, {}),
    adminSetUserEmail: (userId, email) => request('PUT', `/admin/users/${userId}/email`, { email }),
    adminGetAudit: (limit = 200) => request('GET', `/admin/audit?limit=${limit}`),
    adminSetFriendly: (body) => request('POST', '/admin/friendly', body),
    adminGetMatchCoverage: (matchId) => request('GET', `/admin/match/${matchId}/predictions`),
    adminSetKnockoutTeams: (matchId, body) => request('PUT', `/admin/knockout/${matchId}/teams`, body),
    adminGetUserPredictions: (userId) => request('GET', `/admin/users/${userId}/predictions`),
    adminSaveProxyPrediction: (body) => request('POST', '/admin/proxy/prediction', body),
    adminSaveProxyWinner: (body) => request('POST', '/admin/proxy/winner', body),
    adminSaveProxyDefault: (body) => request('POST', '/admin/proxy/default', body),
    adminCreatePlaceholder: (body) => request('POST', '/admin/placeholder', body),
    adminInvitePlaceholder: (userId, body) => request('POST', `/admin/placeholder/${userId}/invite`, body),
    adminDeletePlaceholder: (userId) => request('DELETE', `/admin/placeholder/${userId}`, {}),
    getMatchPredictions: (matchId, leagueId) => request('GET', `/matches/${matchId}/predictions${leagueId ? `?league=${leagueId}` : ''}`),
    getResults: () => request('GET', '/results'),
    getMatchEvents: (matchId) => request('GET', `/matches/${matchId}/events`),
    getFriendlies: () => request('GET', '/friendlies'),
    getKnockoutTeams: () => request('GET', '/knockout/teams'),
    getMatchdayOverrides: () => request('GET', '/matchday-overrides'),
    adminSetMatchdayOverride: (matchId, matchday) => request('PUT', `/admin/match/${matchId}/matchday`, { matchday }),
    adminGetReminderWindow: () => request('GET', '/admin/config/reminder-window'),
    adminSetReminderWindow: (minsBefore, windowMins) => request('PUT', '/admin/config/reminder-window', { minsBefore, windowMins }),
    // Leagues (admin)
    adminGetLeagues: () => request('GET', '/admin/leagues'),
    adminCreateLeague: (body) => request('POST', '/admin/leagues', body),
    adminGetLeague: (leagueId) => request('GET', `/admin/leagues/${leagueId}`),
    adminInviteToLeague: (leagueId, body) => request('POST', `/admin/leagues/${leagueId}/invite`, body),
    adminAddToLeague: (leagueId, body) => request('POST', `/admin/leagues/${leagueId}/members`, body),
    adminRemoveFromLeague: (leagueId, userId) => request('DELETE', `/admin/leagues/${leagueId}/members/${userId}`),
    adminRenameLeague: (leagueId, body) => request('PUT', `/admin/leagues/${leagueId}`, body),
    adminDeleteLeague: (leagueId) => request('DELETE', `/admin/leagues/${leagueId}`),
    // Join requests
    adminGetJoinRequests: () => request('GET', '/admin/join-requests'),
    adminApproveJoinRequest: (body) => request('POST', '/admin/join-requests/approve', body),
    adminDenyJoinRequest: (body) => request('POST', '/admin/join-requests/deny', body),
    // League creation requests
    requestLeague: (body) => request('POST', '/league-request', body),
    adminGetLeagueRequests: () => request('GET', '/admin/league-requests'),
    adminApproveLeagueRequest: (body) => request('POST', '/admin/league-requests/approve', body),
    adminDenyLeagueRequest: (body) => request('POST', '/admin/league-requests/deny', body),
    // League-specific prediction overrides
    removeLeagueOverride: (userId, type, matchId, leagueId) =>
      request('DELETE', type === 'match' ? `/predictions/${userId}/match/${matchId}` : `/predictions/${userId}/${type}`, { leagueId }),
    // User preferences & account management
    getPrefs: (userId) => request('GET', `/predictions/${userId}/prefs`),
    updatePrefs: (userId, body) => request('PUT', `/predictions/${userId}/prefs`, body),
    recordConsent: (userId) => request('POST', `/predictions/${userId}/consent`, {}),
    deleteAccount: (userId) => request('DELETE', `/predictions/${userId}`, {}),
  };
}
